<template>
    <modal ref="modal" id="modal-adjust-monthly" size="sm">
        <template slot="title">{{ t('balance-adjust_monthly_package') }}</template>
        <template slot="body">
            <div class="form-group">
                <label>{{ t('balance-credits') }}:</label>
                <input class="form-control" type="number" v-model.number="local_credits" />
            </div>
            <div class="form-group">
                <label>{{ plan && plan.currency ? plan.currency : 'R$' }}</label>
                <input class="form-control" :disabled="!(user && user.global_admin)" type="tel" v-money="money" v-model="local_price" />
            </div>
            <div class="form-group checkbox-group" v-if="local_credits > initialCredits">
                <label for="apply-monthly-change-now">
                    <input
                        type="checkbox"
                        id="apply-monthly-change-now"
                        v-model="apply_monthly_change_now"
                    />
                    {{ t('balance-apply_monthly_change_now') }}
                </label>
                <div v-if="apply_monthly_change_now && priceDifference > 0" class="small mt-1 text-muted" style="padding-left: 22px;">
                    {{ t('balance-price_difference_charged').replace('{amount}', '') }} <strong class="text-success">{{ formattedPriceDifference }}</strong>
                </div>
            </div>
        </template>
        <template slot="footer">
            <div class="d-flex justify-content-end gap-md">
                <KvButton variant="outlined" @click="close">{{ t('cancel') || 'Cancelar' }}</KvButton>
                <KvTooltip :text="disabledMessage" :disabled="!isButtonDisabled">
                    <KvButton 
                        variant="filled" 
                        :loading="saving || loading" 
                        :disabled="isButtonDisabled"
                        @click.prevent="onClickUpdate"
                    >
                        {{ t('balance-update_button') }}
                    </KvButton>
                </KvTooltip>
            </div>
        </template>
    </modal>
</template>

<script>
import Modal from "../../components/modal/modal";
import KvButton from "../../components/form/kv-button.vue";
import KvTooltip from "../../components/ui/kv-tooltip.vue";
import keviApi from "../../keviApi";

const moneyConfig = {
    decimal: ",",
    thousands: ".",
    prefix: "R$ ",
    suffix: "",
    precision: 2,
    masked: false,
};

export default {
    components: { Modal, KvButton, KvTooltip },
    props: {
        user: { type: Object, default: () => ({}) },
        place: { type: Object, default: () => ({}) },
        plan: { type: Object, default: () => ({}) },
        initialCredits: { type: [Number, String], default: 0 },
        initialPrice: { type: [Number, String], default: 0 },
        saving: { type: Boolean, default: false }
    },
    data() {
        return {
            local_credits: this.initialCredits,
            local_price: this.initialPrice,
            apply_monthly_change_now: false,
            money: moneyConfig,
            loading: false,
        };
    },
    computed: {
        wasChanged() {
            return parseFloat(this.local_credits) !== parseFloat(this.initialCredits) || 
                   this.apply_monthly_change_now === true;
        },
        isButtonDisabled() {
            return !this.wasChanged;
        },
        disabledMessage() {
            if (!this.wasChanged) return this.t('balance-no_changes_detected');
            return "";
        },
        priceDifference() {
            let cleanPrice = typeof this.local_price === 'string' 
                ? parseFloat(this.local_price.replace(/[^\d]/g, '')) / 100 
                : this.local_price;
            if (isNaN(cleanPrice)) cleanPrice = 0;
            
            let currentCleanPrice = typeof this.initialPrice === 'string'
                ? parseFloat(this.initialPrice.toString().replace(/[^\d]/g, '')) / 100
                : this.initialPrice;
            if (isNaN(currentCleanPrice)) currentCleanPrice = 0;

            return cleanPrice - currentCleanPrice;
        },
        formattedPriceDifference() {
            const diff = this.priceDifference;
            if (diff <= 0) return 'R$ 0,00';
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(diff);
        }
    },
    watch: {
        local_credits(newVal) {
            const amount = parseInt(newVal);
            if (amount && this.plan?.pricetable_credits?.length > 0) {
                let pricePerUnit = this.plan.pricetable_credits[this.plan.pricetable_credits.length - 1];
                const filtered = this.plan.pricetable_credits.filter((i) => parseInt(i.interval) >= amount);
                if (filtered.length) pricePerUnit = filtered[0];
                this.local_price = (amount * parseFloat(pricePerUnit?.unitValue || 0)).toFixed(2);
            } else {
                this.local_price = "0.00";
            }
            if (newVal <= this.initialCredits) {
                this.apply_monthly_change_now = false;
            }
        }
    },
    methods: {
        open() { 
            this.local_credits = this.initialCredits;
            this.local_price = this.initialPrice;
            this.apply_monthly_change_now = false;
            this.$refs.modal.open(); 
        },
        close() { this.$refs.modal.close(); },
        async onClickUpdate() {
            if (this.user && this.user.global_admin) {
                this.submit();
            } else {
                if (this.apply_monthly_change_now && this.local_credits > this.initialCredits) {
                    await this.submitClientUpgrade();
                } else {
                    this.openWhatsAppAjuste();
                }
            }
        },
        async submitClientUpgrade() {
            if (this.loading) return;
            this.loading = true;
            try {
                const cleanPrice = typeof this.local_price === 'string' 
                    ? parseFloat(this.local_price.replace(/[^\d]/g, '')) / 100 
                    : this.local_price;
                
                const currentCleanPrice = typeof this.initialPrice === 'string'
                    ? parseFloat(this.initialPrice.replace(/[^\d]/g, '')) / 100
                    : this.initialPrice;

                const payload = {
                    amount_diff: this.local_credits - this.initialCredits,
                    total_diff: cleanPrice - currentCleanPrice
                };

                const options = keviApi.getOptions('invoices/request-monthly-adjustment', 'POST', payload);
                const response = await this.$http(options);

                if (response.status === 200 || response.status === 201) {
                    this.$store.dispatch('notification/success', this.t('balance-generating_invoice'));
                    this.openWhatsAppAjuste();
                } else {
                    this.$store.dispatch('notification/error', this.t('balance-error_processing_invoice'));
                }
            } catch (err) {
                console.error(err);
                this.$store.dispatch('notification/error', this.t('balance-server_communication_error'));
            } finally {
                this.loading = false;
            }
        },
        submit() {
            this.$emit('submit', {
                credits_per_month: this.local_credits,
                price_monthly_credits: this.local_price,
                apply_monthly_change_now: this.apply_monthly_change_now
            });
        },

        openWhatsAppAjuste() {
            const phone = "5548988418958"; 
            const establishment = this.t('balance-whatsapp_msg_establishment').replace('{name}', this.place?.name || this.t('balance-not_informed'));
            const requester = this.t('balance-whatsapp_msg_requester').replace('{name}', this.user?.name || this.t('balance-not_informed'));
            const details = this.t('balance-whatsapp_msg_details_adjust').replace('{credits}', this.local_credits || 0);
            
            const msg = `${this.t('balance-whatsapp_msg_intro')}\n\n${establishment}\n${requester}\n\n${details}.`;
            
            window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
            this.close();
        }
    }
};
</script>

<style scoped lang="scss">
.form-group {
    margin-bottom: 20px;

    label {
        display: block;
        font-size: 14px;
        font-weight: 500;
        color: #111827;
        margin-bottom: 8px;
    }

    input[type="text"], 
    input[type="number"], 
    input[type="tel"] {
        border: 1px solid #E5E7EB;
        border-radius: 8px;
        padding: 10px 14px;
        font-size: 14px;
        width: 100%;
    }
}

.checkbox-group label {
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #111827;
}

.gap-md { gap: 16px; }
</style>
